import { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../context/AuthContext";
import { API_URL } from "../../Constants";

const WebSetting = () => {
  const { user } = useContext(AuthContext);
  const [logo, setLogo] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // NEW STATE: For the hide numbers toggle
  const [isPhoneMasked, setIsPhoneMasked] = useState(false);

  const [formData, setFormData] = useState({
    companyName: "",
    mobileNumber: "",
    email: "",
    gstNo: "",
    panNo: "",
    taxSlabs: "",
    companyAddress: "",
    stateName: "",
    stateCode: "",
    pinCode: "",
    bankDetails: {
      inFavorOf: "",
      bankName: "",
      accountNo: "",
      ifscCode: "",
      paymentMode: "",
    },
  });

  const getProfileImageUrl = useCallback(async (filename) => {
    if (!filename) {
      setLogo(null);
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/api/user/profileImage/${filename}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob",
        }
      );

      const imageUrl = URL.createObjectURL(response.data);
      setLogo(imageUrl);
    } catch (error) {
      console.error("Error loading profile image:", error);
      setLogo(null);
    }
  });

  // Fetch existing data on component mount
  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const fetchCompanyProfile = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API_URL}/api/user/details/${user?.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const data = response.data;

      if (data.profileImage) {
        await getProfileImageUrl(data.profileImage);
      }

      // Populate mask state if it comes from the profile API
      if (data.maskLeadPhone !== undefined) {
        setIsPhoneMasked(data.maskLeadPhone);
      }

      setFormData({
        companyName: data.companyName || "",
        mobileNumber: data.mobileNumber || "",
        email: data.email || "",
        gstNo: data.gstNo || "",
        panNo: data.panNo || "",
        taxSlabs: data.taxSlabs || "",
        companyAddress: data.companyAddress || "",
        stateName: data.stateName || "",
        stateCode: data.stateCode || "",
        pinCode: data.pinCode || "",
        bankDetails: data.bankDetails || {
          inFavorOf: "",
          bankName: "",
          accountNo: "",
          ifscCode: "",
          paymentMode: "",
        },
      });
    } catch (error) {
      toast.error("Failed to fetch company profile");
      console.error("Error fetching profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setLogoFile(file);
      setLogo(URL.createObjectURL(file));
    }
  };

  const handleBankDetailsChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails, // Keep all existing values
        [name]: value, // Update the changed field
      },
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // NEW HANDLER: For the API integration of the slider
  const handleToggleMaskPhone = async () => {
    const newValue = !isPhoneMasked;
    // Optimistically update UI
    setIsPhoneMasked(newValue);

    try {
      await axios.put(
        `${API_URL}/api/user/${user?.id}/mask-lead-phone?enable=${newValue}`,
        {}, // Empty body for PUT
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success(`Number masking turned ${newValue ? "ON" : "OFF"}`);
    } catch (error) {
      // Revert UI on failure
      setIsPhoneMasked(!newValue);
      toast.error("Failed to update number masking setting");
      console.error("Error updating mask setting:", error);
    }
  };

  const validateForm = () => {
    if (!formData.companyName.trim()) {
      toast.error("Company name is required");
      return false;
    }
    if (!formData.taxSlabs.trim() || isNaN(formData.taxSlabs)) {
      toast.error("Tax Slab must be a valid number");
      return false;
    }
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (
      formData.gstNo &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        formData.gstNo
      )
    ) {
      toast.error("Please enter a valid GST number");
      return false;
    }
    if (formData.panNo && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNo)) {
      toast.error("Please enter a valid PAN number");
      return false;
    }
    if (formData.pinCode && !/^\d{6}$/.test(formData.pinCode)) {
      toast.error("PIN Code must be a 6-digit number");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setIsLoading(true);

      // First update the profile image if changed
      if (logoFile) {
        const imageFormData = new FormData();
        imageFormData.append("profileImage", logoFile);

        await axios.put(
          `${API_URL}/api/user/updateProfileImage/${user?.id}`,
          imageFormData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }

      // Prepare the data to send
      const dataToSend = {
        ...formData,
        bankDetails: formData.bankDetails, // Keep the array structure
      };

      await axios.put(
        `${API_URL}/api/user/updateCompanyDetails/${user?.id}`,
        dataToSend,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success("Profile updated successfully!");
      fetchCompanyProfile(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update profile");
      console.error("Error updating profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className=" mx-auto p-4 md:p-6 bg-[#FAF8FF] dark:bg-gray-900 dark:text-white shadow-md cfg-font">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
      <h2 className="text-2xl font-bold cfg-heading text-gray-800 dark:text-white text-center mb-6">
        Company Profile Settings
      </h2>

      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#673AB7] mx-auto"></div>
            <p className="mt-3 text-center">Saving changes...</p>
          </div>
        </div>
      )}

      {/* Logo Upload */}
      <div className="flex justify-center mb-8">
        <div className="text-center">
          <div className="w-32 h-32 mx-auto rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden relative group">
            {logo ? (
              <>
                <img
                  src={logo}
                  alt="Company Logo"
                  className="w-full h-full object-contain p-2"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100">
                  <span className="text-white text-sm font-medium">
                    Change Logo
                  </span>
                </div>
              </>
            ) : (
              <span className="text-gray-400 text-sm">Upload Logo</span>
            )}
          </div>
          <label className="mt-3 inline-flex items-center px-4 py-2 bg-[#673AB7]  text-white rounded-md text-sm font-medium cursor-pointer transition-colors shadow-sm">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            Choose Image
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </label>
          <p className="mt-1 text-xs text-gray-500">
            Recommended size: 300x300px (Max 5MB)
          </p>
        </div>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {/* Company Information */}
        <div className="md:col-span-2">
          <h3 className="text-lg font-semibold cfg-heading text-gray-700 mb-4 pb-2 border-b border-gray-200 dark:text-white">
            Company Information
          </h3>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
            Company Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-[#673AB7] text-sm transition dark:bg-gray-700 dark:text-white"
            placeholder="Enter company name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-[#673AB7] text-sm transition dark:bg-gray-700 dark:text-white"
            placeholder="Enter company email"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
            Mobile Number
          </label>
          <input
            type="tel"
            name="mobileNumber"
            value={formData.mobileNumber}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-[#673AB7] text-sm transition dark:bg-gray-700 dark:text-white"
            placeholder="Enter mobile number"
            pattern="[0-9]{10}"
            maxLength="10"
          />
        </div>

        {/* Bank Details Section */}
        <div className="md:col-span-2 mt-4">
          <h3 className="text-lg font-semibold cfg-heading text-gray-700 mb-4 pb-2 border-b border-gray-200 dark:text-white">
            Bank Details
          </h3>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 dark:text-white">
            In Favor Of
          </label>
          <input
            name="inFavorOf"
            value={formData.bankDetails?.inFavorOf || ""}
            onChange={handleBankDetailsChange}
            className="border rounded px-2 py-1 text-sm w-full dark:text-white dark:bg-gray-700"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 dark:text-white">
            Bank Name
          </label>
          <input
            name="bankName"
            value={formData.bankDetails?.bankName || ""}
            onChange={handleBankDetailsChange}
            className="border rounded px-2 py-1 text-sm w-full dark:text-white dark:bg-gray-700"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 dark:text-white">
            Account No.
          </label>
          <input
            name="accountNo"
            value={formData.bankDetails?.accountNo || ""}
            onChange={handleBankDetailsChange}
            className="border rounded px-2 py-1 text-sm w-full dark:text-white dark:bg-gray-700"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 dark:text-white">
            IFSC Code
          </label>
          <input
            name="ifscCode"
            value={formData.bankDetails?.ifscCode || ""}
            onChange={handleBankDetailsChange}
            className="border rounded px-2 py-1 text-sm w-full dark:text-white dark:bg-gray-700"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 dark:text-white">
            Payment Mode
          </label>
          <input
            name="paymentMode"
            value={formData.bankDetails?.paymentMode || ""}
            onChange={handleBankDetailsChange}
            className="border rounded px-2 py-1 text-sm w-full dark:text-white dark:bg-gray-700"
          />
        </div>

        {/* Tax Information */}
        <div className="md:col-span-2 mt-4">
          <h3 className="text-lg font-semibold cfg-heading text-gray-700 mb-4 pb-2 border-b border-gray-200 dark:text-white">
            Tax Information
          </h3>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
            GST Number
          </label>
          <input
            type="text"
            name="gstNo"
            value={formData.gstNo}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-[#673AB7] text-sm transition dark:bg-gray-700 dark:text-white"
            placeholder="22AAAAA0000A1Z5"
            maxLength="15"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
            PAN Number
          </label>
          <input
            type="text"
            name="panNo"
            value={formData.panNo}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-[#673AB7] text-sm transition dark:bg-gray-700 dark:text-white"
            placeholder="AAAAA0000A"
            maxLength="10"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
            Tax Slabs(%)
          </label>
          <input
            type="number"
            name="taxSlabs"
            value={formData.taxSlabs}
            onChange={handleChange}
            min="0"
            className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-[#673AB7] text-sm transition dark:bg-gray-700 dark:text-white"
            placeholder="Example 5"
            maxLength="10"
          />
        </div>

        {/* Address Information */}
        <div className="md:col-span-2 mt-4">
          <h3 className="text-lg font-semibold cfg-heading text-gray-700 mb-4 pb-2 border-b border-gray-200 dark:text-white">
            Address Information
          </h3>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
            Company Address
          </label>
          <textarea
            name="companyAddress"
            value={formData.companyAddress}
            onChange={handleChange}
            rows="3"
            placeholder="Enter full company address"
            className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-[#673AB7] text-sm transition resize-none dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
            State
          </label>
          <input
            type="text"
            name="stateName"
            value={formData.stateName}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-[#673AB7] text-sm transition dark:bg-gray-700 dark:text-white"
            placeholder="Enter state"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
            State Code
          </label>
          <input
            type="text"
            name="stateCode"
            value={formData.stateCode}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-[#673AB7] text-sm transition dark:bg-gray-700 dark:text-white"
            placeholder="Enter state code"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
            PIN Code
          </label>
          <input
            type="text"
            name="pinCode"
            value={formData.pinCode}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-[#673AB7] text-sm transition dark:bg-gray-700 dark:text-white"
            placeholder="Enter PIN code"
            maxLength="6"
            pattern="[0-9]{6}"
          />
        </div>

        {/* NEW COMPONENT: Hide Numbers Toggle */}
        <div className="md:col-span-2 mt-4 flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
          <div>
            <h3 className="text-md font-semibold cfg-heading text-gray-800 dark:text-white">
              Hide numbers from executive
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              When enabled, executives will not see lead phone numbers.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={isPhoneMasked}
              onChange={handleToggleMaskPhone}
            />
            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#E9D5FF] dark:peer-focus:ring-[#5E35A6] rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-500 peer-checked:bg-[#673AB7]"></div>
          </label>
        </div>

        <div className="md:col-span-2 flex justify-end pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mr-3 px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#8B5CF6] transition shadow-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#673AB7] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#8B5CF6] transition disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WebSetting;